/**
 * ADR-0048 — whether THIS person, on THIS build, may promote a catalogue item
 * to production, and what the button should say.
 *
 * Pure: the server decides for real (functions/lib/catalogue_promotion.py
 * refuses every case this file hides). This exists so the training planner
 * doesn't offer a button that would be refused, and so the wording of the
 * offer is testable without a browser.
 */
import type { AccessRole } from "../admin/claims";

/** Only the training build shows it — production has nothing to promote to,
 *  and demo/develop are not authoring surfaces (ADR-0048 §1). */
export const PROMOTE_DATABASE = "training";

/** Mirrors PROMOTE_ROLES in functions/lib/catalogue_promotion.py. */
export const PROMOTE_ROLES: ReadonlyArray<string> = ["admin", "practice-author"];

export function canPromote(
  databaseId: string,
  role: AccessRole | string | null | undefined,
): boolean {
  return databaseId === PROMOTE_DATABASE && PROMOTE_ROLES.includes(String(role ?? ""));
}

/**
 * What the row says about an item's promotion state.
 *
 * `promotionCount` comes from the item's own `_provenance` — training's copy
 * carries it once production has adopted the item and the nightly sync has
 * brought production's version back down (ADR-0048 §4: production is
 * canonical afterwards).
 */
export function promotionStatusText(provenance: {
  origin?: string;
  promotedAt?: string;
  promotionCount?: number;
} | null | undefined): string {
  const count = provenance?.promotionCount ?? 0;
  if (!provenance || provenance.origin !== "training" || count < 1) {
    return "Not in production yet.";
  }
  const when = (provenance.promotedAt ?? "").slice(0, 10);
  const times = count === 1 ? "once" : `${count} times`;
  return when
    ? `In production — promoted ${times}, last on ${when}.`
    : `In production — promoted ${times}.`;
}

export function promoteButtonLabel(provenance: { origin?: string; promotionCount?: number } | null | undefined): string {
  const promoted = provenance?.origin === "training" && (provenance?.promotionCount ?? 0) >= 1;
  return promoted ? "Promote again" : "Promote to production";
}
